import { ChevronFirst, ChevronLast } from "lucide-react"
import logo from "../assets/logo.png"
import profile from "../assets/profile.png"
import React, { createContext, useContext, useState, cloneElement } from "react"

import { useNavigate } from 'react-router-dom';
import { useSession } from '../assets/auth/SessionContext';

const SidebarContext = createContext();

export default function Sidebar({ expanded, setExpanded, children }) {
    const session = JSON.parse(localStorage.getItem("session"));

    return (
        <aside className="h-screen fixed" style={{ zIndex: 100 }}>
            <nav
                className="h-full flex flex-col"
                style={{
                    background: "#0f172a",
                    width: expanded ? "260px" : "72px",
                    transition: "width 0.25s ease",
                    overflowX: "hidden",
                    boxShadow: "4px 0 24px rgba(0,0,0,0.18)",
                }}
            >
                {/* Header / Logo area */}
                <div
                    style={{
                        borderBottom: "4px solid transparent",
                        backgroundImage: "linear-gradient(#0f172a, #0f172a), linear-gradient(135deg, #1e3a5f, #2563eb)",
                        backgroundOrigin: "border-box",
                        backgroundClip: "padding-box, border-box",
                        padding: "16px 12px 12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: "64px",
                    }}
                >
                    <div
                        style={{
                            overflow: "hidden",
                            transition: "all 0.25s ease",
                            width: expanded ? "140px" : "0px",
                            opacity: expanded ? 1 : 0,
                        }}
                    >
                        <img
                            src={logo}
                            alt="TDI Logo"
                            style={{
                                width: "120px",
                                display: "block",
                            }}
                        />
                    </div>
                    <button
                        onClick={() => setExpanded(curr => !curr)}
                        style={{
                            background: "rgba(255,255,255,0.1)",
                            border: "none",
                            borderRadius: "8px",
                            padding: "6px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff",
                            transition: "background 0.2s",
                            flexShrink: 0,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                    >
                        {expanded ? <ChevronFirst size={18} /> : <ChevronLast size={18} />}
                    </button>
                </div>

                {/* Nav items */}
                <SidebarContext.Provider value={{ expanded }}>
                    <ul
                        className="flex-1"
                        style={{
                            padding: "8px 10px",
                            margin: 0,
                            listStyle: "none",
                            overflowY: "auto",
                            overflowX: "hidden",
                        }}
                    >
                        {children}
                    </ul>
                </SidebarContext.Provider>

                {/* User footer */}
                <div
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        padding: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    <img
                        src={profile}
                        alt=""
                        style={{
                            width: "38px",
                            height: "38px",
                            borderRadius: "8px",
                            flexShrink: 0,
                            objectFit: "cover",
                            border: "2px solid rgba(255,255,255,0.12)",
                        }}
                    />
                    <div
                        style={{
                            overflow: "hidden",
                            transition: "all 0.25s ease",
                            width: expanded ? "180px" : "0px",
                            opacity: expanded ? 1 : 0,
                        }}
                    >
                        <div style={{ lineHeight: 1.3 }}>
                            <div
                                style={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "#ffffff",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {session?.user?.displayName}
                            </div>
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: "#94a3b8",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    marginTop: "2px",
                                }}
                            >
                                {session?.user?.email}
                            </div>
                            {session?.user?.role && (
                                <div
                                    style={{
                                        display: "inline-block",
                                        marginTop: "4px",
                                        background: "rgba(37,99,235,0.25)",
                                        color: "#93c5fd",
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        padding: "1px 7px",
                                        borderRadius: "20px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.4px",
                                    }}
                                >
                                    {session.user.role}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
        </aside>
    );
}

export function SidebarItem({ id, icon, text, active, path, action, children, isOpen, onToggle }) {
    const { expanded } = useContext(SidebarContext);
    const { logout } = useSession();
    const navigate = useNavigate();

    const handleClick = (e) => {
        e.stopPropagation();

        if (children) {
            onToggle(id);
        } else if (path) {
            if (onToggle) {
                onToggle(id);
            }
            navigate(path);
        } else if (action === "logout") {
            logout();
        }
    };

    const activeStyle = {
        background: "linear-gradient(135deg, #D72B63, #2563eb)",
        color: "#ffffff",
        boxShadow: "0 4px 15px rgba(215,43,99,0.35)",
        borderRadius: "10px",
    };

    const defaultStyle = {
        color: "#94a3b8",
        borderRadius: "10px",
    };

    return (
        <li style={{ listStyle: "none", position: "relative" }}>
            <div
                onClick={handleClick}
                className="max-height-side-panel group"
                style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    padding: "9px 10px",
                    margin: "3px 0",
                    fontWeight: 500,
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    ...(active ? activeStyle : defaultStyle),
                }}
                onMouseEnter={e => {
                    if (!active) {
                        e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                        e.currentTarget.style.color = "#ffffff";
                    }
                }}
                onMouseLeave={e => {
                    if (!active) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "#94a3b8";
                    }
                }}
            >
                {/* Icon */}
                <span
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        width: "20px",
                        height: "20px",
                    }}
                >
                    {icon}
                </span>

                {/* Label */}
                <span
                    style={{
                        overflow: "hidden",
                        transition: "all 0.25s ease",
                        width: expanded ? "180px" : "0px",
                        opacity: expanded ? 1 : 0,
                        marginLeft: expanded ? "10px" : "0px",
                        whiteSpace: "nowrap",
                    }}
                >
                    {text}
                </span>

                {/* Collapsed tooltip */}
                {!expanded && (
                    <div
                        className="sidebar-tooltip"
                        style={{
                            position: "absolute",
                            left: "calc(100% + 12px)",
                            background: "#1e293b",
                            color: "#ffffff",
                            fontSize: "12px",
                            fontWeight: 500,
                            padding: "5px 10px",
                            borderRadius: "6px",
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                            opacity: 0,
                            transform: "translateX(-6px)",
                            transition: "all 0.18s ease",
                            zIndex: 999,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                        }}
                    >
                        {text}
                    </div>
                )}
            </div>

            {/* Sub-items */}
            {expanded && isOpen && children && (
                <ul
                    className="max-width-side-panel"
                    style={{
                        listStyle: "none",
                        margin: "2px 0 2px 14px",
                        padding: "0 0 0 12px",
                        borderLeft: "2px solid rgba(255,255,255,0.12)",
                    }}
                >
                    {React.Children.toArray(children).map(child =>
                        React.cloneElement(child, {
                            _isSubItem: true,
                        })
                    )}
                </ul>
            )}

            <style>{`
                .group:hover .sidebar-tooltip {
                    opacity: 1 !important;
                    transform: translateX(0) !important;
                }
            `}</style>
        </li>
    );
}
